import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import BeforeAfterComparison from './BeforeAfterComparison';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROOM_TYPES = [
  { value: 'all', label: 'Tous les projets' },
  { value: 'cuisine', label: 'Cuisine' },
  { value: 'salle_de_bain', label: 'Salle de bain' },
  { value: 'complete', label: 'Rénovation complète' },
  { value: 'partielle', label: 'Rénovation partielle' },
  { value: 'peinture', label: 'Peinture' },
  { value: 'parquet', label: 'Parquet' },
  { value: 'carrelage', label: 'Carrelage' }
];

export default function GallerySection() {
  const [cases, setCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchCases = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/gallery-cases`);
      setCases(response.data);
      setFilteredCases(response.data);
    } catch (error) {
      toast.error('Impossible de charger la galerie');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  useEffect(() => {
    if (selectedFilter === 'all') {
      setFilteredCases(cases);
    } else {
      setFilteredCases(cases.filter(c => c.room_type === selectedFilter));
    }
  }, [selectedFilter, cases]);

  return (
    <div className="space-y-6">
      {/* Header + Filtre */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="font-['Playfair_Display'] text-2xl font-semibold mb-2" style={{ color: 'var(--hr-text)' }}>
            Galerie avant / après
          </h3>
          <p className="text-sm" style={{ color: 'var(--hr-text-muted)' }}>
            {filteredCases.length} projet{filteredCases.length > 1 ? 's' : ''} trouvé{filteredCases.length > 1 ? 's' : ''}
          </p>
        </div>
        
        <Select value={selectedFilter} onValueChange={setSelectedFilter}>
          <SelectTrigger 
            className="w-full sm:w-[240px] rounded-xl border-[color:var(--hr-border)]" 
            data-testid="gallery-filter-select"
          >
            <SelectValue placeholder="Filtrer par type" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            {ROOM_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Grid des cas */}
      {loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2].map(i => (
            <Card key={i} className="rounded-2xl">
              <CardHeader>
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-[300px] w-full rounded-xl" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {!loading && filteredCases.length === 0 && (
        <Card className="rounded-2xl border-[color:var(--hr-border)]">
          <CardContent className="py-12 text-center">
            <p className="text-base" style={{ color: 'var(--hr-text-muted)' }}>
              Aucun projet trouvé. Utilisez l'onglet "Test upload" pour créer des cas de test.
            </p>
          </CardContent>
        </Card>
      )}
      
      {!loading && filteredCases.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredCases.map(caseItem => (
            <Card 
              key={caseItem.id} 
              className="rounded-2xl border-[color:var(--hr-border)] hover:shadow-[0_18px_48px_rgba(43,43,43,0.10)] transition-shadow"
              data-testid="gallery-case-card"
            >
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <CardTitle className="font-['Playfair_Display'] text-xl mb-1" style={{ color: 'var(--hr-text)' }}>
                      {caseItem.title}
                    </CardTitle>
                    <CardDescription className="text-sm" style={{ color: 'var(--hr-text-muted)' }}>
                      {caseItem.location && `${caseItem.location} • `}
                      {caseItem.duration_weeks && `${caseItem.duration_weeks} semaines`}
                    </CardDescription>
                  </div>
                  {caseItem.featured && (
                    <Badge 
                      className="rounded-full font-['Montserrat'] text-xs"
                      style={{ backgroundColor: 'var(--hr-accent)', color: 'var(--hr-accent-fg)' }}
                    >
                      En vedette
                    </Badge>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <BeforeAfterComparison 
                  beforeImage={`${BACKEND_URL}${caseItem.before_image_url}`}
                  afterImage={`${BACKEND_URL}${caseItem.after_image_url}`}
                  alt={caseItem.title}
                />
                
                {caseItem.what_was_managed && (
                  <div className="pt-2">
                    <p className="text-xs font-['Montserrat'] font-medium uppercase tracking-wide mb-1" style={{ color: 'var(--hr-text-muted)' }}>
                      Ce qui a été piloté
                    </p>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--hr-text)' }}>
                      {caseItem.what_was_managed}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}